import { addYears } from 'date-fns/addYears';
import { startOfDay } from 'date-fns/startOfDay';
import { toDate } from 'date-fns/toDate';
import observer from 'saddlebag-observer';

import customSet from '../../../utils/dateHandling/customSet';
import {
  HOTEL_SELECT_MONTH,
  SELECT_DAY_FOR_HOTELS,
} from '../helpers/constants/actionsConstants';
import * as constants from '../helpers/constants/dataTypeConstants';
import validators from '../helpers/dateSelectionValidators';

import type {
  HotelDateSelectionAction,
  SelectDayForHotelAction,
  SelectMonthForHotelAction,
} from '../actions/types';
import type { HotelDateSelectionState } from '../selectors/types';

const today = () => startOfDay(new Date());

const initialState: HotelDateSelectionState = {
  minCheckInDate: today(),
  minCheckOutDate: today(),
  checkOutMonthDate: null,
  checkInMonthDate: today(),
  minDate: today(),
  maxDate: addYears(today(), 1),
  selectedCheckIn: today(),
  selectedCheckOut: null,
  wholeYear: false,
};

const validateSelectDayArgs = (action: SelectDayForHotelAction) => {
  const { dayOfMonth } = action.payload;
  if (
    !Number.isInteger(dayOfMonth) ||
    dayOfMonth < constants.MIN_MONTH_DAY ||
    dayOfMonth > constants.MAX_MONTH_DAY
  ) {
    throw new Error(`Invalid dayOfMonth provided: ${dayOfMonth}`);
  }
};

const validateSelectMonthArgs = (action: SelectMonthForHotelAction) => {
  const { month } = action.payload;
  if (
    !Number.isInteger(month) ||
    month < constants.MIN_MONTH ||
    month > constants.MAX_MONTH
  ) {
    throw new Error(`Invalid month provided: ${month}`);
  }
};

const formatDay = (date: Date) => `0${date.getDate()}`.slice(-2);

const yearMonth = (date?: Date) => {
  if (!date) return '';
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  return `${year.toString().slice(-2)}${`0${month.toString()}`.slice(-2)}`;
};

const reduceSelectDay = (
  state: HotelDateSelectionState,
  action: SelectDayForHotelAction,
): HotelDateSelectionState => {
  validateSelectDayArgs(action);

  if (action.payload.direction === constants.OUTBOUND_DIRECTION) {
    const newCheckIn = toDate(state.selectedCheckIn);
    newCheckIn.setDate(action.payload.dayOfMonth);
    observer.publish('update-hotel-checkin-day', formatDay(newCheckIn));

    const newDates: Partial<HotelDateSelectionState> = {
      selectedCheckIn: newCheckIn,
      minCheckOutDate: newCheckIn,
      wholeYear: false,
    };

    if (state.selectedCheckOut && newCheckIn > state.selectedCheckOut) {
      newDates.selectedCheckOut = newCheckIn;
      observer.publish('update-hotel-checkout-day', formatDay(newCheckIn));
    }

    return { ...state, ...newDates };
  }

  if (action.payload.direction === constants.INBOUND_DIRECTION) {
    if (!state.selectedCheckOut) return state;
    const newCheckOut = toDate(state.selectedCheckOut);
    newCheckOut.setDate(action.payload.dayOfMonth);
    observer.publish('update-hotel-checkout-day', formatDay(newCheckOut));
    return {
      ...state,
      selectedCheckOut: newCheckOut,
      wholeYear: false,
    };
  }

  return state;
};

const reduceSelectMonth = (
  state: HotelDateSelectionState,
  action: SelectMonthForHotelAction,
): HotelDateSelectionState => {
  validateSelectMonthArgs(action);
  const newMonthDate = customSet(
    action.payload.year,
    action.payload.month - 1,
    1,
  );

  if (action.payload.direction === constants.OUTBOUND_DIRECTION) {
    const newDates = validators.getHotelValidDatesOnOutboundMonthChange(
      newMonthDate,
      state.checkOutMonthDate,
    );

    observer.publish(
      'update-hotel-checkin-year-month',
      yearMonth(newDates.selectedCheckIn),
    );
    if (newDates.checkOutMonthDate) {
      observer.publish(
        'update-hotel-checkout-year-month',
        yearMonth(newDates.checkOutMonthDate),
      );
    }
    return { ...state, ...newDates, wholeYear: false };
  }

  if (action.payload.direction === constants.INBOUND_DIRECTION) {
    const newDates = validators.getHotelValidDatesOnInboundMonthChange(
      state.checkInMonthDate,
      newMonthDate,
    );

    observer.publish(
      'update-hotel-checkout-year-month',
      yearMonth(newDates.checkOutMonthDate),
    );
    if (newDates.checkInMonthDate) {
      observer.publish(
        'update-hotel-checkin-year-month',
        yearMonth(newDates.checkInMonthDate),
      );
    }
    return { ...state, ...newDates, wholeYear: false };
  }

  return state;
};

const reducer = (
  state: HotelDateSelectionState = initialState,
  action: HotelDateSelectionAction,
): HotelDateSelectionState => {
  switch (action.type) {
    case SELECT_DAY_FOR_HOTELS:
      return reduceSelectDay(state, action);

    case HOTEL_SELECT_MONTH:
      return reduceSelectMonth(state, action);

    default:
      return state;
  }
};

export default reducer;
